import { AzureManagementServiceApiRef, aaPingSSOAuthApiRef } from '../../services/';
import { useApi } from '@backstage/core-plugin-api';
import { BucketId, ClientId, PluginId } from '@runway/devkit-common';
import { SimpleForm, aadGroupStatus } from './SimpleForm';
import { SimpleFormValues } from './schema';
import { useTrackedRequest } from '../../hooks';
import { FC, useState } from 'react';

type SimpleRequestFormProps = {
  onSuccess?: (req: {
    activeDirectoryGroupName: string;
    tenant: string;
    users: { employee_id: string; user_type: string }[];
  }) => void;
};

export const SimpleRequestForm: FC<SimpleRequestFormProps> = ({ onSuccess }: SimpleRequestFormProps) => {
  const ams = useApi(AzureManagementServiceApiRef);
  const ssoApi = useApi(aaPingSSOAuthApiRef);

  const { tracker } = useTrackedRequest({
    bucket_id: BucketId.USE_METRIC,
    client_id: ClientId.RUNWAY_FRONTEND,
    pluginId: PluginId.ACTIVE_DIRECTORY_GROUP.id,
  });

  const [aadProcessing, setAzureActiveDirectoryProcessing] = useState(aadGroupStatus.awaitingInput);
  const [GroupName, setAzureActiveDirectoryGroupName] = useState('');
  const [outputMsg, setOutputMsg] = useState('');

  const handleSubmitRequestClick = async (values: SimpleFormValues) => {
    setAzureActiveDirectoryProcessing(aadGroupStatus.processing);

    const reqUsers = values.users.map(user => {
      return { employee_id: user.id!, user_type: user.userType! };
    });

    const req = {
      activeDirectoryGroupName: values.activeDirectoryGroupName!,
      tenant: values.tenant,
      users: reqUsers,
    };

    try {
      const identity = await ssoApi.getAAProfile({
        optional: true,
        instantPopup: true,
      });

      const response = await tracker(
        async () => {
          return await ams.createAzureActiveDirectoryGroup(req);
        },
        {
          groupName: values.activeDirectoryGroupName!,
          groupOwner: JSON.stringify(values.users),
          userEmployeeId: identity?.aaId ? identity.aaId : '-1',
        },
      );

      if (response.includes('has been created successfully')) {
        setAzureActiveDirectoryGroupName(`Azure Active Directory Group Name: ${GroupName}`);
        setAzureActiveDirectoryProcessing(aadGroupStatus.success);
        if (onSuccess) onSuccess(req);
      }
      setOutputMsg(response);
    } catch (e: any) {
      setOutputMsg(e.message || 'Error communicating with backend services.');
      setAzureActiveDirectoryGroupName('Error communicating with backend services.');
      setAzureActiveDirectoryProcessing(aadGroupStatus.failure);
    }
  };

  return <SimpleForm status={aadProcessing} outputMsg={outputMsg} onSubmit={handleSubmitRequestClick} />;
};
